// urlBuilder is the single source of truth for all API endpoint URLs.
// All domain API modules must use these builders — no hardcoded URL strings elsewhere.
//
// Design: urlBuilder owns the base host (read from ENV vars); all builders return absolute URLs.
// API clients (oocApi, oloApi) do not set a baseURL — they receive absolute URLs from here.
// This means a missing urlBuilder call produces an obvious failure rather than silently
// resolving via a client-level baseURL fallback.
//
// Adding a new API family: follow the oocBase/oocUrls pattern below.

// ---------------------------------------------------------------------------
// OOC (Online Ordering Connector)
// ---------------------------------------------------------------------------

const oocBase = (): string => {
	const base = import.meta.env.VITE_OOC_API_BASE_URL
	if (!base?.trim()) {
		throw new Error(
			'[urlBuilder] VITE_OOC_API_BASE_URL is not defined. Ensure it is set in your environment.',
		)
	}
	return base.trim().replace(/\/+$/, '')
}

const oocMars = (): string => `${oocBase()}/online-ordering/mars`

export const oocUrls = {
	featureFlags: (): string => `${oocMars()}/api/v2/feature-flags`,

	store: (ref: string, isDineIn = false): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}${isDineIn ? '?dineIn=true' : ''}`,
	vanityStore: (vanityRef: string): string =>
		`${oocMars()}/api/v2/stores/vanity/${encodeURIComponent(vanityRef)}`,

	// Menu — scheduleId selects the service schedule; modelName selects the menu model
	menu: (ref: string, scheduleId: string, modelName: string): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}/menu/${encodeURIComponent(scheduleId)}/${encodeURIComponent(modelName)}`,

	validateDistance: (ref: string): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}/validate-distance`,

	// Note: ref is placed directly under the version prefix, not under /stores/
	timeSlots: (ref: string): string =>
		`${oocMars()}/api/v3/${encodeURIComponent(ref)}/availability/time-slots`,
	schedules: (ref: string): string =>
		`${oocMars()}/api/v3/${encodeURIComponent(ref)}/availability/schedules`,

	placeOrder: (ref: string): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}/order`,

	loyaltyMembers: (ref: string): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}/loyalty/members`,
	loyaltyPrograms: (ref: string): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}/loyalty/programs`,
	loyaltyMemberPoint: (ref: string): string =>
		`${oocMars()}/api/v2/stores/${encodeURIComponent(ref)}/loyalty/members/point`,

	signIn: (): string => `${oocMars()}/api/v2/customer/auth`,
	signUp: (): string => `${oocMars()}/api/v2/customer/registration`,
	forgotPassword: (): string => `${oocMars()}/api/v2/customer/forgot-password`,
	resetPassword: (): string => `${oocMars()}/api/v2/customer/reset-password`,
	changePassword: (): string => `${oocMars()}/api/v2/customer/password`,
	logout: (): string => `${oocMars()}/api/v3/customer/logout`,

	// OTP-based authentication (V2)
	requestOtp: (): string => `${oocMars()}/api/v3/customer/auth/request-otp`,
	verifyOtp: (): string => `${oocMars()}/api/v3/customer/auth/validate-otp`,

	// session-scoped — identity from JWT, no :id segment
	customer: (): string => `${oocMars()}/api/v3/customer`,
	customerDeliveryAddresses: (): string => `${oocMars()}/api/v3/customer/delivery-addresses`,
	customerPaymentMethods: (): string => `${oocMars()}/api/v3/customer/payment-methods`,
}

// ---------------------------------------------------------------------------
// OLO (legacy API)
// ---------------------------------------------------------------------------

const oloBase = (): string => {
	const base = import.meta.env.VITE_OLO_API_BASE_URL
	if (!base?.trim()) {
		throw new Error(
			'[urlBuilder] VITE_OLO_API_BASE_URL is not defined. Ensure it is set in your environment.',
		)
	}
	return base.trim().replace(/\/+$/, '')
}

/**
 * Validates that all required API base URL env vars are present at app startup.
 * Call this once in main.tsx before rendering — a missing var throws immediately
 * rather than surfacing mid-user-flow when the first API call is made.
 */
export function validateEnv(): void {
	oocBase()
	oloBase()
}

export const oloUrls = {
	// Payment
	authorizePayment: (ref: string): string =>
		`${oloBase()}/api/v3/public/${encodeURIComponent(ref)}/payment/authorize`,

	// Order
	placeOrder: (ref: string): string =>
		`${oloBase()}/api/v3/public/${encodeURIComponent(ref)}/order`,
	// Note: SSE status stream uses api/v3
	orderStatus: (orderRef: string): string =>
		`${oloBase()}/api/v3/public/order/${encodeURIComponent(orderRef)}/status`,

	// Past orders (customer history)
	pastOrderItems: (ref: string, scheduleId: string): string =>
		`${oloBase()}/api/v3/public/${encodeURIComponent(ref)}/order/${encodeURIComponent(scheduleId)}/customer/past-order-items`,
	pastOrderItem: (ref: string, scheduleId: string, itemRef: string, itemGuid: string): string =>
		`${oloBase()}/api/v3/public/${encodeURIComponent(ref)}/order/${encodeURIComponent(scheduleId)}/customer/past-order-item/${encodeURIComponent(itemRef)}/id/${encodeURIComponent(itemGuid)}`,

	// Menu
	menuCategories: (ref: string, scheduleId: string): string =>
		`${oloBase()}/api/v2/public/${encodeURIComponent(ref)}/menu/${encodeURIComponent(scheduleId)}/categories`,
	menuItems: (ref: string, scheduleId: string): string =>
		`${oloBase()}/api/v2/public/${encodeURIComponent(ref)}/menu/${encodeURIComponent(scheduleId)}/items`,

	// Item schedules
	itemSchedules: (ref: string, itemRef: string): string =>
		`${oloBase()}/api/v2/public/${encodeURIComponent(ref)}/schedules/item/${encodeURIComponent(itemRef)}`,

	// Item detail (includes modifierSets)
	menuItemDetail: (ref: string, guid: string): string =>
		`${oloBase()}/api/v2/public/${encodeURIComponent(ref)}/menu/item/${encodeURIComponent(guid)}`,

	// Cart
	calculateCart: (ref: string): string =>
		`${oloBase()}/api/v2/public/${encodeURIComponent(ref)}/cart/calculate`,

	giftCardBalance: (ref: string, code: string): string =>
		`${oloBase()}/api/v1/public/${encodeURIComponent(ref)}/gift/balance?code=${encodeURIComponent(code)}`,

	// Loyalty rewards — OLO v1 (Ember: adapters/discount.js uses ENV.host, not oocHost)
	discounts: (ref: string): string =>
		`${oloBase()}/api/v1/public/discounts/${encodeURIComponent(ref)}`,
}
